import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Badge from 'components/Badge';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import DataTable, { CellPrimary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import PageContainer from 'components/PageContainer';
import {
  HeaderContainer,
  HeaderTitle,
  HeaderSubtitle,
} from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import SearchInput from 'components/SearchInput';
import TableFilter from 'components/TableFilter';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { AppContext } from 'shared/AppContext';
import { useGetTrackEvents } from '../Events/gql/get-track-events';

const TrackTransactions = () => {
  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);

  // Filter state - pending (shown in filter panel) vs applied (used in query)
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  const { pathname } = useLocation();
  const isTrackAdmin = pathname.includes('/admin-track');
  const isEmployeeAdmin = pathname.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  const {
    state: { accountName },
  } = useContext(AppContext);

  // Fetch events data with date filters
  const { data, loading } = useGetTrackEvents(appliedStartDate, appliedEndDate);

  useEffect(() => {
    if (data?.getEventsByAccount) {
      setEvents(data.getEventsByAccount.results || []);
      setCount(data.getEventsByAccount.count || 0);
    }
  }, [data]);

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle filter change - updates pending state
  const handleFilterChange = (newFilters) => {
    if (newFilters.startDate !== undefined) {
      setPendingStartDate(newFilters.startDate || '');
    }
    if (newFilters.endDate !== undefined) {
      setPendingEndDate(newFilters.endDate || '');
    }
  };

  // Handle filter apply - updates applied state to trigger query
  const handleApplyFilters = (newFilters) => {
    const newStartDate = newFilters.startDate ?? pendingStartDate;
    const newEndDate = newFilters.endDate ?? pendingEndDate;

    setAppliedStartDate(newStartDate);
    setAppliedEndDate(newEndDate);
    setPendingStartDate(newStartDate);
    setPendingEndDate(newEndDate);

    updateQuery({ page: 1 });
  };

  // Handle filter clear
  const handleClearFilters = () => {
    setAppliedStartDate('');
    setAppliedEndDate('');
    setPendingStartDate('');
    setPendingEndDate('');
    updateQuery({ page: 1 });
  };

  // Build filter configuration for TableFilter
  const filterConfig = [
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'date',
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'date',
    },
  ];

  // Current filter values for TableFilter
  const filterValues = {
    startDate: pendingStartDate,
    endDate: pendingEndDate,
  };

  // Get status badge variant
  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'published':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'draft':
        return 'warning';
      case 'postponed':
        return 'warning';
      case 'complete':
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  // DataTable columns
  const columns = [
    {
      field: 'date',
      header: 'Date',
      mobileSubtitle: true,
      render: (event) => {
        const { isMultiDay, day, date, month, listDates, year } = event;
        return isMultiDay
          ? `${month?.toUpperCase()} ${listDates} - ${year}`
          : `${day} ${date} - ${year}`;
      },
    },
    {
      field: 'name',
      header: 'Event',
      mobileTitle: true,
      render: (event) => <CellPrimary>{event.name}</CellPrimary>,
    },
    {
      field: 'track',
      header: 'Track',
      mobileSubtitle: true,
      render: (event) => event.track?.name || '-',
    },
    {
      field: 'status',
      header: 'Status',
      render: (event) => (
        <Badge variant={getStatusVariant(event.status)}>
          {event.status
            ? event.status.charAt(0).toUpperCase() +
              event.status.slice(1).toLowerCase()
            : 'N/A'}
        </Badge>
      ),
    },
  ];

  // DataTable actions
  const actions = [
    {
      type: 'view',
      title: 'View Transactions',
      mobileLabel: 'View',
      to: (event) => `${routePrefix}/transactions/event/${event.id}`,
    },
  ];

  const totalPages = Math.ceil(count / 15);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="transactions" />
            <div>
              <HeaderTitle>Transactions</HeaderTitle>
              <HeaderSubtitle>
                {accountName || 'Account'} - Event Transactions
              </HeaderSubtitle>
            </div>
          </HeaderContainer>
        </Card>

        {/* Content Card */}
        <Card noPadding>
          <ContentWrapper>
            {/* Page Header with Search and Date Filter */}
            <PageToolbar>
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search by Event Name..."
                debounceMs={500}
              />
              <TableFilter
                filters={filterConfig}
                values={filterValues}
                onChange={handleFilterChange}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
              />
            </PageToolbar>

            {/* DataTable */}
            <DataTable
              columns={columns}
              data={events}
              loading={loading}
              keyField="id"
              emptyMessage="No events found"
              emptySubtext="Try adjusting your search or filters"
              actions={actions}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </ContentWrapper>
        </Card>
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackTransactions;
